import { resolveLocale } from "~/i18n/currency";

/**
 * Canonical money formatter for the backoffice.
 *
 * `currency` is REQUIRED — there is intentionally no INR default, so a call site
 * can never silently fall back to INR (the bug this i18n work fixes). Locale is
 * derived from the currency unless explicitly overridden.
 *
 * `style` defaults to "decimal" to preserve the historical digit-only output
 * (callers that prepend their own symbol). Use `style: "currency"` for surfaces
 * that should render the locale-correct currency symbol via Intl.
 *
 * `decimalRound` defaults to 0 — round-figure output. PDF exports pass
 * `minimumFractionDigits`/`decimalRound` explicitly for fixed "x.00" output.
 */
export interface FormatMoneyOptions {

  /** ISO currency code, e.g. "INR" | "USD" | "NPR". Required — no default. */
  currency: string;

  /** BCP-47 locale; resolved from `currency` when omitted. */
  locale?: string;

  /** Maximum fraction digits. Default 0 (round figure). */
  decimalRound?: number;

  /** Minimum fraction digits (used by PDF exports for fixed "x.00" output). */
  minimumFractionDigits?: number;

  /** "decimal" (default, digits only) or "currency" (locale-correct symbol). */
  style?: "decimal" | "currency";

  /**
   * Sign display. Default (undefined) = Intl "auto" — minus on negatives only.
   * Use "exceptZero" for P&L surfaces that need an explicit "+" on positives;
   * Intl places the sign locale-correctly, so never prepend "+"/"-" by hand.
   */
  signDisplay?: Intl.NumberFormatOptions["signDisplay"];
}

export const formatMoney = (value: number | undefined | null, options: FormatMoneyOptions): string => {
  const { currency, locale, decimalRound = 0, minimumFractionDigits, style = "decimal", signDisplay } = options;
  const resolvedLocale = locale ?? resolveLocale(currency);

  const n = Number(value);
  if (!Number.isFinite(n)) return style === "currency" ? "" : "0";

  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style,
      ...(style === "currency" ? { currency } : {}),
      maximumFractionDigits: decimalRound,
      ...(minimumFractionDigits != null ? { minimumFractionDigits } : {}),
      ...(signDisplay != null ? { signDisplay } : {}),
    }).format(n);
  } catch {
    // An invalid ISO currency code throws RangeError under style:"currency".
    // Degrade to decimal grouping (digits only) instead of crashing the render.
    return new Intl.NumberFormat(resolvedLocale, {
      maximumFractionDigits: decimalRound,
      ...(minimumFractionDigits != null ? { minimumFractionDigits } : {}),
      ...(signDisplay != null ? { signDisplay } : {}),
    }).format(n);
  }
};
